// One approval or question card, in whatever state it is in.
//
// - Pending: Approve or Deny an approval, with an optional note for the agent; Answer or Dismiss a
//   question (Dismiss sends deny, which the server takes for questions). Approve carries out
//   exactly what the card says, under the decider's own permissions, which the server checks
//   again. Any active member may decide any card of the agent, a subagent's included. Numbers
//   restart per run (a run and its subagent can both have a #1), so a card is known by its id.
//   While its agent can't take decisions (paused, suspended: the `locked` prop) a pending card
//   says why in place of its buttons.
// - Waiting for the server's answer: "Sending..." when approving it sends something out of
//   NotesAI (card.sends: a text, an email or a fax, which can take 60 s, 90 s for a fax), else
//   "Working...". The request is never cut short on a timer.
// - Being decided, decided, expired, or being checked: who decided it and when (in the viewer's
//   own time), and what NotesAI found when it checked an unsure outcome itself (card.check, which
//   carries no value: {state, source, found_at, checked_at, next_at, gives_up_at, reason?}). A card
//   it is checking has no buttons; one it found didn't happen, or couldn't confirm, is pending again.
//
// What the server answers a decision with is shown as it comes (checking, or expired when the
// sweep took the card first), until the parent passes a newer card; an answer that comes after the
// parent has read a newer card (realtime got there first) gives way to it at once. Refusals are
// shown through agentErrorText, each until the card changes; after one that retrying won't change
// (a 403; a 503 that won't pass, such as agents being switched off) the buttons stay off. After a
// refusal that may mean the card or its agent changed (any 409, a 403, a 404, a 500), or an answer
// that never came back (a dropped connection, a gateway's own error page), the card asks for the
// agent to be read again (onRefresh). An answer that never came back shows no failure: the card
// stays busy until the agent has been read again (when onRefresh returns a promise), then shows
// where it stands. Neither does the 409 for a card NotesAI is checking.
//
// Props:
// - card: the card as the agent detail gives it;
// - names: {user_id: name}, for "Approved by ..." (anyone else is "a member");
// - locked: null, or the sentence a pending card shows instead of its buttons while its agent can't
//   take decisions (for example "Ask the owner to resume it.");
// - onDecided(card | null): the server took a decision; `card` is the card as it answered;
// - onRefresh(): read the agent again; may return a promise. Without it, onDecided(null) is called.
import React, { useRef, useState } from 'react';
import { decideCard } from './agentsService';
import { agentErrorCanRetry, agentErrorText } from './agentErrors';
import { reasonWords, sourceName } from './checkWords';
import { lostAnswer } from './usePlanRequest';

// The characters the words use that aren't ASCII, so this file stays ASCII.
const ELLIPSIS = String.fromCharCode(0x2026);
const DOT = String.fromCharCode(0xb7);
const TICK = String.fromCharCode(0x2713);
const WARNING = String.fromCharCode(0x26a0);

const OPEN_CARD = 'rounded-lg border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/[0.08] p-2.5 space-y-2';
const OPEN_TITLE = 'text-[11px] font-bold text-amber-800 dark:text-amber-300';
const CLOSED_CARD = 'rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-2.5 space-y-1.5';
const NUMBER = 'text-[11px] font-bold text-slate-500 dark:text-slate-400';
const TEXT = 'text-xs text-slate-800 dark:text-slate-100 whitespace-pre-wrap break-words';
const TEXT_FADED = 'text-xs text-slate-500 dark:text-slate-400 whitespace-pre-wrap break-words';
const FIELD = 'w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-800 dark:text-slate-100 disabled:opacity-60';
const BUTTON = 'rounded-md px-2.5 py-1 text-[11px] font-semibold disabled:opacity-50';
const YES = 'bg-emerald-600 dark:bg-emerald-600 hover:bg-emerald-500 dark:hover:bg-emerald-500 text-white';
const ANSWER = 'bg-blue-600 hover:bg-blue-500 text-white';
const NO = 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600';
const PILL = 'px-1.5 py-0.5 rounded text-[10px] font-bold';
const TONES = new Map([
  ['slate', 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'],
  ['emerald', 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300'],
  ['rose', 'bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300'],
  ['blue', 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300'],
]);
const TONE_OF = new Map([['approved', 'emerald'], ['denied', 'rose'], ['answered', 'blue']]);
const LINE = 'text-[11px] text-slate-700 dark:text-slate-300';
const FADED_LINE = 'text-[11px] text-slate-500 dark:text-slate-400';
const DONE_LINE = 'text-[11px] text-emerald-700 dark:text-emerald-300';
const WARN_LINE = 'flex items-start gap-1.5 text-[11px] text-amber-800 dark:text-amber-300';
const STATUS_LINE = 'flex items-start gap-2 text-[11px] text-slate-700 dark:text-slate-200';
const ERROR = 'text-[11px] text-red-600 dark:text-red-400';

const EXPIRED = 'Nobody decided in time; it expired.';
// An expired card with a decider is an old one (before NotesAI checked unsure outcomes itself):
// its decision was cut off while its action was being carried out.
const INTERRUPTED = 'It was interrupted while being carried out.';

// cards.py, through checks.CHECKING_CARD: a decision on a card NotesAI is checking. It is told by
// its code once the server sends one ({"error", "code": "checking"}), else by its fixed opening
// words. The card read again says "Checking..." itself, so the refusal needs no words of its own.
const CHECKING_REFUSAL = /^NotesAI is checking whether this went through\b/;
// The refusals after which the card, or its agent, may have changed: the agent is read again.
const READ_AGAIN = new Set([403, 404, 409, 500]);

const capital = (words) => words.charAt(0).toUpperCase() + words.slice(1);
const checkOf = (card) => (card && card.check && typeof card.check === 'object' ? card.check : null);
// Where a check looked, by name (checkWords, as the steps name it).
const sourceOf = (check) => sourceName(check?.source);
const nameOf = (names, id) => {
  const name = names && Object.prototype.hasOwnProperty.call(names, id) ? names[id] : null;
  return typeof name === 'string' && name.trim() ? name : 'a member';
};
// An answer that never came back (lostAnswer, shared with the chat: a dropped connection, or a
// gateway's page in front of the server): the decision may or may not have been taken.
const checkingRefusal = (err) => err?.status === 409
  && (err?.payload?.code === 'checking' || CHECKING_REFUSAL.test(String(err?.message || '')));
// A refusal that deciding again won't change: the decider may not decide it (403), or agents
// can't be used on this server (a 503 agentErrorCanRetry says won't pass).
const wontPass = (err) => err?.status === 403 || (err?.status === 503 && !agentErrorCanRetry(err));
// What a card is now, for telling a newer card from the one an answer or a refusal came with.
const stateOf = (card) => JSON.stringify([card?.id, card?.status, card?.decided_by, card?.decided_at,
  card?.expires_at, checkOf(card)]);
// Asks the parent to read the agent again and waits for that, when it returns a promise. A read
// that fails, or throws, is the parent's to show: the card then shows what the parent has.
async function settled(read) {
  try {
    await read();
  } catch {
    // the parent's own trouble
  }
}

const TIME = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const WEEKDAY = new Intl.DateTimeFormat('en-US', { weekday: 'short' });
const MONTH_DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const FULL_DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const DAY_MS = 24 * 60 * 60 * 1000;
const midnight = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

// An ISO time in the viewer's own zone: "09:05" today, "Tue 08:41" up to six days either side,
// else "Oct 3, 08:41" ("Oct 3, 2025, 08:41" in another year). Null for a time that can't be read.
export function localTime(iso, now = Date.now()) {
  if (typeof iso !== 'string' || !iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const today = new Date(now);
  const time = TIME.format(at);
  const days = Math.round((midnight(today) - midnight(at)) / DAY_MS);
  if (days === 0) return time;
  if (Math.abs(days) <= 6) return `${WEEKDAY.format(at)} ${time}`;
  const date = at.getFullYear() === today.getFullYear() ? MONTH_DAY.format(at) : FULL_DATE.format(at);
  return `${date}, ${time}`;
}

// Who decided a card, and when: "Approved by Dr. Lee", a middle dot, then "09:05" ("Approved by the
// orchestrator: <its reason>" for one an orchestrator decided); "Left as it
// was by ..." for an approval left as it was after NotesAI couldn't confirm it ("Leave it");
// "Being decided by ..."; "Expired". A card being checked was approved: NotesAI is checking what
// the approval did.
export function decisionText(card, names = {}) {
  // An orchestrator in Auto decides some of its agents' cards itself: those say so, and why.
  const byOrchestrator = typeof card?.orchestrator_reason === 'string';
  const who = byOrchestrator ? 'the orchestrator' : (card?.decided_by ? nameOf(names, card.decided_by) : null);
  const why = byOrchestrator && card.orchestrator_reason ? `: ${card.orchestrator_reason}` : '';
  const time = localTime(card?.decided_at);
  const tail = `${who ? ` by ${who}` : ''}${why}${time ? ` ${DOT} ${time}` : ''}`;
  switch (card?.status) {
    case 'approved':
    case 'checking':
      return `Approved${tail}`;
    case 'denied':
      if (card.kind === 'question') return `Dismissed${tail}`;
      return `${checkOf(card)?.state === 'unknown' ? 'Left as it was' : 'Denied'}${tail}`;
    case 'answered':
      return `Answered${tail}`;
    case 'deciding':
      return `Being decided${who ? ` by ${who}` : ''}`;
    case 'expired':
      return 'Expired';
    default:
      return card?.status ? capital(String(card.status)) : 'Waiting';
  }
}

// Why a check couldn't confirm it: the check's reason code, in the server's own words (checkWords,
// shared with the steps; a missing or unknown code reads as "it can't be looked up").
function reasonFor(check) {
  return reasonWords(check?.reason);
}

// The line a pending approval shows once NotesAI has checked its unsure outcome: it didn't happen
// (plain words, and the usual Approve and Deny), or it couldn't be confirmed (amber: deciding it
// again may do it twice).
function recheckNote(card) {
  const check = checkOf(card);
  const source = sourceOf(check);
  if (check?.state === 'not_happened') {
    return {
      warn: false,
      words: card.sends
        ? `It didn't go out${source ? `, and ${source} has no record of it` : ''}. Approve to send it now.`
        : "It wasn't done, so nothing changed. Approve to do it now.",
    };
  }
  if (check?.state === 'unknown') {
    const reason = reasonFor(check);
    return {
      warn: true,
      words: card.sends
        ? `Couldn't confirm whether this went out: ${reason}. Sending it again may send a duplicate.`
        : `Couldn't confirm whether this was done: ${reason}. Doing it again may do it twice.`,
    };
  }
  return null;
}

// What a check found, under a card that is no longer pending: it went through (an approved card),
// or (a denied or expired one) it didn't, or it couldn't be confirmed and wasn't done again. A card
// still marked checking whose check has found something (the card's own update comes just after
// its step's) says what was found, not "Checking...".
function outcome(card) {
  const check = checkOf(card);
  const source = sourceOf(check);
  const { status } = card;
  if (check?.state === 'happened' && (status === 'approved' || status === 'checking')) {
    const time = localTime(check.found_at);
    const at = time ? ` at ${time}` : '';
    let words;
    if (card.sends) words = source ? `Sent. ${capital(source)} confirms it went out${at}.` : `Sent. NotesAI confirmed it went out${at}.`;
    else words = source ? `Done: ${source} shows it went through${at}.` : `Done: NotesAI confirmed it went through${at}.`;
    return { tone: DONE_LINE, words: `${TICK} ${words}` };
  }
  if (check?.state === 'unknown' && status === 'checking') {
    return {
      tone: FADED_LINE,
      words: card.sends ? "NotesAI couldn't confirm whether it went out." : "NotesAI couldn't confirm whether it was done.",
    };
  }
  if (check?.state === 'unknown' && (status === 'denied' || status === 'expired')) {
    return {
      tone: FADED_LINE,
      words: card.sends
        ? "NotesAI couldn't confirm whether it went out, and it wasn't sent again."
        : "NotesAI couldn't confirm whether it was done, and it wasn't done again.",
    };
  }
  if (check?.state === 'not_happened' && (status === 'denied' || status === 'expired' || status === 'checking')) {
    return { tone: FADED_LINE, words: card.sends ? "It didn't go out." : "It wasn't done, so nothing changed." };
  }
  return null;
}

function Spinner({ className, faint }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity={faint} />
      <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function Clock() {
  return (
    <svg className="w-3.5 h-3.5 mt-px shrink-0 text-slate-400 dark:text-slate-500" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

// A card NotesAI is checking: what it is checking, and with whom; once a check has come back
// without an answer (its next one is due after it), that the source hasn't confirmed it yet, and
// until when NotesAI keeps checking. No buttons, and no red.
function Checking({ card }) {
  const check = checkOf(card);
  const source = sourceOf(check);
  const checked = Date.parse(check?.checked_at);
  const next = Date.parse(check?.next_at);
  if (Number.isFinite(checked) && Number.isFinite(next) && next > checked) {
    const until = localTime(check.gives_up_at);
    return (
      <div role="status" className={STATUS_LINE}>
        <Clock />
        <span>{`${source ? `${capital(source)} hasn't confirmed it yet.` : "It isn't confirmed yet."} NotesAI will keep checking${until ? ` until ${until}` : ''}.`}</span>
      </div>
    );
  }
  return (
    <div role="status" className={STATUS_LINE}>
      <Spinner className="w-3.5 h-3.5 mt-px shrink-0 animate-spin text-blue-600 dark:text-blue-400" faint=".25" />
      <div>
        <div className="font-semibold">{card.sends ? `Checking whether it went out${ELLIPSIS}` : `Checking whether it was done${ELLIPSIS}`}</div>
        {source && <div className="text-slate-500 dark:text-slate-400">{`Checking with ${source}${ELLIPSIS}`}</div>}
      </div>
    </div>
  );
}

export default function CardItem({ card, names = {}, locked = null, onDecided, onRefresh }) {
  const [note, setNote] = useState('');
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(null); // the decision waiting for its answer
  const [reply, setReply] = useState(null); // {card, over}: the server's card, shown while the parent's is `over`
  const [error, setError] = useState(null); // {text, over, final}: a refusal, shown while the card is `over`
  const inFlight = useRef(false);
  const given = useRef(card);
  given.current = card;
  // A refusal belongs to the agent as it was: once its agent is locked or unlocked, it goes (a
  // "paused" refused just before the pause showed must not come back once the agent is resumed).
  const lockedNow = typeof locked === 'string' && locked.trim() ? locked : null;
  const [lockSeen, setLockSeen] = useState(lockedNow);
  if (lockSeen !== lockedNow) {
    setLockSeen(lockedNow);
    setError(null);
  }

  const shown = reply && reply.over === stateOf(card) && reply.card.id === card?.id ? reply.card : card;
  const showing = useRef(shown);
  showing.current = shown;
  if (!shown) return null;

  const readAgain = () => (onRefresh ? onRefresh() : onDecided?.(null));

  const refused = (err) => {
    if (!checkingRefusal(err)) setError({ text: agentErrorText(err), over: stateOf(showing.current), final: wontPass(err) });
    if (READ_AGAIN.has(err?.status)) readAgain();
  };

  const decide = async (decision) => {
    if (inFlight.current) return;
    inFlight.current = true;
    const target = showing.current;
    // The card as it was clicked, as shown and as the parent had it. The server's answer is shown
    // over the card only while the parent still has one of these, or the claim this decision makes
    // (deciding): never over a newer card the parent read meanwhile.
    const fromShown = stateOf(target);
    const fromParent = stateOf(given.current);
    const asked = target.kind === 'question';
    // A card that couldn't be confirmed shows no note field, so it sends no note.
    const noteShown = !asked && recheckNote(target)?.warn !== true;
    setBusy(decision);
    setError(null);
    let answered = null;
    let failure = null;
    try {
      answered = await decideCard(target.id, {
        decision,
        note: noteShown ? note.trim() || null : null,
        answer: asked && decision === 'answer' ? answer.trim() || null : null,
      });
    } catch (err) {
      failure = err || new Error('');
    }
    // No answer came back, so whether it was decided is unknown: the card stays busy until the
    // agent has been read again, so it isn't decided twice meanwhile.
    const lost = failure !== null && lostAnswer(failure);
    if (lost) await settled(readAgain);
    inFlight.current = false;
    setBusy(null);
    if (failure) {
      if (!lost) refused(failure);
      return;
    }
    const after = answered && answered.card && typeof answered.card === 'object' ? answered.card : null;
    const current = stateOf(given.current);
    const keep = current === fromShown || current === fromParent || given.current?.status === 'deciding';
    setReply(after && keep ? { card: after, over: current } : null);
    // Taken: should the card come back to be decided again (a check found it didn't happen), it
    // starts empty, as the server cleared its note.
    setNote('');
    setAnswer('');
    onDecided?.(after);
  };

  const status = shown.status;
  const asked = shown.kind === 'question';
  const text = asked ? shown.question : shown.description;
  const number = shown.number ? `#${shown.number}` : '';
  const shownError = error && error.over === stateOf(shown) ? error : null;
  const errorLine = shownError && <div role="alert" className={ERROR}>{shownError.text}</div>;

  if (busy === null && status && status !== 'pending') {
    const line = outcome(shown);
    return (
      <div className={CLOSED_CARD}>
        <div className="flex items-center justify-between gap-2">
          <span className={NUMBER}>{number}</span>
          <span className={`${PILL} ${TONES.get(TONE_OF.get(status) || 'slate')}`}>{decisionText(shown, names)}</span>
        </div>
        <div className={status === 'expired' ? TEXT_FADED : TEXT}>{text}</div>
        {status === 'checking' && (!checkOf(shown) || checkOf(shown).state === 'checking') && <Checking card={shown} />}
        {status === 'expired' && <div className={FADED_LINE}>{shown.decided_by ? INTERRUPTED : EXPIRED}</div>}
        {line && <div className={line.tone}>{line.words}</div>}
        {errorLine}
      </div>
    );
  }

  const recheck = asked ? null : recheckNote(shown);
  const unsure = recheck?.warn === true;
  const recheckLine = recheck && (recheck.warn ? (
    <div className={WARN_LINE}><span aria-hidden="true">{WARNING}</span>{recheck.words}</div>
  ) : (
    <div className={LINE}>{recheck.words}</div>
  ));
  const title = `${number ? `${number} ` : ''}${asked ? 'The agent asks' : 'Waiting for your OK'}`;

  // Its agent can't take decisions now: the reason, in place of the note and the buttons.
  if (busy === null && lockedNow) {
    return (
      <div className={OPEN_CARD}>
        <div className={OPEN_TITLE}>{title}</div>
        <div className={TEXT}>{text}</div>
        {recheckLine}
        <div className={LINE}>{lockedNow}</div>
      </div>
    );
  }

  const off = busy !== null || shownError?.final === true;
  const sending = busy === 'approve' && !!shown.sends;
  let yes = 'Approve';
  if (busy === 'approve') yes = sending ? `Sending${ELLIPSIS}` : `Working${ELLIPSIS}`;
  else if (unsure) yes = shown.sends ? 'Send it again' : 'Do it again';
  let no = asked ? 'Dismiss' : 'Deny';
  if (busy === 'deny') no = `Working${ELLIPSIS}`;
  else if (unsure) no = 'Leave it';

  return (
    <div className={OPEN_CARD}>
      <div className={OPEN_TITLE}>{title}</div>
      <div className={TEXT}>{text}</div>
      {recheckLine}
      {asked && (
        <textarea aria-label="Your answer" rows={2} className={FIELD} value={answer} disabled={busy !== null}
                  onChange={(e) => setAnswer(e.target.value)} />
      )}
      {!asked && !unsure && (
        <input aria-label="Note for the agent" className={FIELD} placeholder="Optional note for the agent" value={note}
               disabled={busy !== null} onChange={(e) => setNote(e.target.value)} />
      )}
      {errorLine}
      <div className="flex gap-2">
        {asked ? (
          <button type="button" disabled={off || !answer.trim()} onClick={() => decide('answer')}
                  className={`${BUTTON} ${ANSWER}`}>
            {busy === 'answer' ? `Working${ELLIPSIS}` : 'Answer'}
          </button>
        ) : (
          <button type="button" disabled={off} onClick={() => decide('approve')}
                  className={`${BUTTON} ${YES}${sending ? ' inline-flex items-center gap-1.5' : ''}`}>
            {sending && <Spinner className="w-3 h-3 animate-spin" faint=".3" />}
            {yes}
          </button>
        )}
        <button type="button" disabled={off} onClick={() => decide('deny')} className={`${BUTTON} ${NO}`}>{no}</button>
      </div>
    </div>
  );
}
